import config from 'config';
import storage from 'helpers/storage';

// Auth model: after sign-in the frontend POSTs its Firebase ID token to
// the backend's /auth/session endpoint; the backend then sets an httpOnly
// `ptr_session` cookie (not reachable by JS, XSS-safe). A sibling
// non-httpOnly `ptr_auth=1` cookie is set alongside so the SPA can answer
// "am I logged in?" synchronously without touching the real credential.
const AUTH_FLAG_COOKIE = 'ptr_auth';

const readCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const pairs = document.cookie.split(';');
  for (let i = 0; i < pairs.length; i += 1) {
    const [rawKey, ...rest] = pairs[i].split('=');
    if (rawKey && rawKey.trim() === name) {
      return decodeURIComponent(rest.join('=').trim());
    }
  }
  return null;
};

const API_BASE = (() => {
  // On localhost we proxy through /__api to dodge backend CORS (see
  // src/setupProxy.js). Production hits config.backendUrl directly.
  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const url = isLocalDev ? '/__api' : (config.backendUrl || '');
  return url.endsWith('/') ? url : `${url}/`;
})();

export const hasAuthToken = () => Boolean(readCookie(AUTH_FLAG_COOKIE));

// Combined auth-presence check. Returns true if the user has a backend session
// cookie (normal verified users) OR a legacy localStorage ID token (anonymous
// Firebase users, which the backend's /auth/session endpoint rejects). Use
// this in place of the old `storage.getItem('token')` boolean check.
export const isAuthenticated = () => hasAuthToken() || Boolean(storage.getItem('token'));

export const clearAuthToken = async () => {
  try {
    await fetch(`${API_BASE}auth/session`, {
      method: 'DELETE',
      credentials: 'include'
    });
  } catch (err) {
    console.error('logout request failed:', err);
  }
};

// Exchange a freshly issued Firebase ID token for an httpOnly session cookie.
// Must be called immediately after Firebase sign-in (auth_time must be within
// the last 5 minutes).
export const setAuthToken = async (idToken) => {
  if (!idToken) return clearAuthToken();
  try {
    const res = await fetch(`${API_BASE}auth/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken })
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error?.message || `session exchange failed: ${res.status}`);
    }
  } catch (err) {
    console.error('session exchange failed:', err);
    throw err;
  }
};
